export type ImageFormat = 'png' | 'jpg' | 'webp' | 'gif' | 'bmp' | 'tiff' | 'ico' | 'svg' | 'avif' | 'heic';
export type OutputFormat = Exclude<ImageFormat, 'avif' | 'heic'>;
export const imageFormats: { id: ImageFormat; label: string; mime: string; extensions: string[]; output: boolean }[] = [
  { id: 'png', label: 'PNG', mime: 'image/png', extensions: ['png'], output: true },
  { id: 'jpg', label: 'JPG', mime: 'image/jpeg', extensions: ['jpg', 'jpeg', 'jfif'], output: true },
  { id: 'webp', label: 'WebP', mime: 'image/webp', extensions: ['webp'], output: true },
  { id: 'gif', label: 'GIF', mime: 'image/gif', extensions: ['gif'], output: true },
  { id: 'bmp', label: 'BMP', mime: 'image/bmp', extensions: ['bmp'], output: true },
  { id: 'tiff', label: 'TIFF', mime: 'image/tiff', extensions: ['tif', 'tiff'], output: true },
  { id: 'ico', label: 'ICO', mime: 'image/x-icon', extensions: ['ico'], output: true },
  { id: 'svg', label: 'SVG', mime: 'image/svg+xml', extensions: ['svg'], output: true },
  { id: 'avif', label: 'AVIF', mime: 'image/avif', extensions: ['avif'], output: false },
  { id: 'heic', label: 'HEIC / HEIF', mime: 'image/heic', extensions: ['heic', 'heif'], output: false },
];
export const IMAGE_ACCEPT = imageFormats.flatMap(format => format.extensions.map(extension => '.' + extension)).join(',');
export const MAX_FILES = 50;
export const MAX_FILE_BYTES = 40 * 1024 * 1024;
export const MAX_BATCH_BYTES = 150 * 1024 * 1024;
export const MAX_OUTPUT_BYTES = 250 * 1024 * 1024;
export const MAX_PIXELS = 40_000_000;

export function detectImageFormat(file: Pick<File, 'name' | 'type'>): ImageFormat | null {
  const extension = file.name.split('.').pop()?.toLowerCase();
  return imageFormats.find(format => format.extensions.includes(extension || ''))?.id
    || imageFormats.find(format => format.mime === file.type)?.id || null;
}

export function checkImageSize(width: number, height: number) {
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1 || width > 32767 || height > 32767 || width * height > MAX_PIXELS) {
    throw new Error('Ảnh tối đa 40 megapixel, mỗi cạnh tối đa 32.767 px.');
  }
}

export function validateBatch(files: Pick<File, 'name' | 'type' | 'size'>[], from: ImageFormat | 'auto' = 'auto') {
  if (!files.length) throw new Error('Hãy thêm ít nhất một ảnh.');
  if (files.length > MAX_FILES) throw new Error('Tối đa 50 ảnh mỗi lần.');
  let total = 0;
  for (const file of files) {
    const format = detectImageFormat(file);
    if (!format) throw new Error(file.name + ': định dạng ảnh chưa được hỗ trợ.');
    if (from !== 'auto' && format !== from) throw new Error(file.name + ': khác định dạng From đã chọn. Chọn Tự nhận diện để dùng ảnh nhiều loại.');
    if (!file.size || file.size > MAX_FILE_BYTES) throw new Error(file.name + ': file phải có dữ liệu và không quá 40 MB.');
    total += file.size;
  }
  if (total > MAX_BATCH_BYTES) throw new Error('Tổng ảnh tối đa 150 MB mỗi lần.');
}

export function outputNames(names: string[], format: OutputFormat): string[] {
  const reserved = new Set<string>();
  return names.map(name => {
    const stem = (name.replace(/\.[^.]+$/, '').replace(/[<>:"/\\|?*\u0000-\u001f]/g, '_').replace(/[. ]+$/, '').slice(0, 120) || 'image');
    const safe = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])$/i.test(stem) ? '_' + stem : stem;
    let result = safe + '.' + format;
    let index = 2;
    while (reserved.has(result.toLowerCase())) result = safe + '_' + index++ + '.' + format;
    reserved.add(result.toLowerCase());
    return result;
  });
}
